import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { PdfService } from './pdf.service';

export interface PdfGeneratorConfig {
  reportTitle?: string;
  reportInfo?: { label: string; value: string }[];
  headers?: string[];
  tableData?: any[][];
  details?: { label: string; value: string }[];
  type?: 'financial' | 'service-order' | 'custom';
  customData?: any;
}

export interface FinancialReportData {
  contasAPagarRecords: any[];
  contasAReceberRecords: any[];
  contasAPagarTotal: number;
  contasAReceberTotal: number;
  selectedRecords: any[];
  generatedAt: Date;
}

@Component({
  selector: 'app-pdf-generator',
  templateUrl: './pdf-generator.component.html',
  styleUrls: ['./pdf-generator.component.sass'],
})
export class PdfGeneratorComponent implements OnInit {
  @Input() data: any; // Dados recebidos para geração do PDF
  @Input() config: PdfGeneratorConfig = {}; // Configuração do relatório
  @Input() showPreview: boolean = true; // Se deve mostrar preview
  @Input() autoGenerate: boolean = false; // Se deve gerar automaticamente
  @Input() fileName: string = 'documento.pdf'; // Nome do arquivo
  
  @Output() pdfGenerated = new EventEmitter<Blob>();
  @Output() generationError = new EventEmitter<string>();

  previewUrl: string | undefined;
  isGenerating: boolean = false;
  errorMessage: string | null = null;

  constructor(private pdfService: PdfService) {}

  ngOnInit(): void {
    if (this.autoGenerate && this.data) {
      this.generatePDF();
    } else if (this.showPreview && this.data) {
      this.generatePreview();
    }
  }

  // Gera a pré-visualização do PDF
  generatePreview(): void {
    if (!this.data) {
      this.errorMessage = 'Nenhum dado fornecido para geração do PDF';
      this.generationError.emit(this.errorMessage);
      return;
    }

    this.isGenerating = true;
    this.errorMessage = null;

    try {
      const pdfConfig = this.buildPdfConfig();
      
      this.pdfService.generatePreview(pdfConfig)
        .then((url) => {
      this.previewUrl = url;
          this.isGenerating = false;
        })
        .catch((error) => {
          console.error('Erro ao gerar preview:', error);
          this.errorMessage = 'Erro ao gerar pré-visualização do PDF';
          this.generationError.emit(this.errorMessage);
          this.isGenerating = false;
        });
    } catch (error) {
      console.error('Erro inesperado ao gerar preview:', error);
      this.errorMessage = 'Erro inesperado ao gerar PDF';
      this.generationError.emit(this.errorMessage);
      this.isGenerating = false;
    }
  }

  // Baixa o PDF gerado
  downloadPDF(): void {
    if (this.isGenerating) {
      return;
    }

    this.isGenerating = true;
    this.errorMessage = null;

    try {
      const pdfConfig = this.buildPdfConfig();
      
      this.pdfService.generateStyledPDF(pdfConfig)
        .then((pdfBlob) => {
          this.downloadFile(pdfBlob);
          this.pdfGenerated.emit(pdfBlob);
          this.isGenerating = false;
        })
        .catch((error) => {
          console.error('Erro ao gerar PDF:', error);
          this.errorMessage = 'Erro ao gerar PDF para download';
          this.generationError.emit(this.errorMessage);
          this.isGenerating = false;
        });
    } catch (error) {
      console.error('Erro inesperado ao gerar PDF:', error);
      this.errorMessage = 'Erro inesperado ao gerar PDF';
      this.generationError.emit(this.errorMessage);
      this.isGenerating = false;
    }
  }

  // Gera PDF sem download (para uso programático)
  generatePDF(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.data) {
        const error = 'Nenhum dado fornecido para geração do PDF';
        this.errorMessage = error;
        this.generationError.emit(error);
        reject(error);
        return;
      }

      this.isGenerating = true;
      this.errorMessage = null;

      try {
        const pdfConfig = this.buildPdfConfig();
        
        this.pdfService.generateStyledPDF(pdfConfig)
          .then((pdfBlob) => {
            this.pdfGenerated.emit(pdfBlob);
            this.isGenerating = false;
            resolve(pdfBlob);
          })
          .catch((error) => {
            console.error('Erro ao gerar PDF:', error);
            this.errorMessage = 'Erro ao gerar PDF';
            this.generationError.emit(this.errorMessage);
            this.isGenerating = false;
            reject(error);
          });
      } catch (error) {
        console.error('Erro inesperado ao gerar PDF:', error);
        this.errorMessage = 'Erro inesperado ao gerar PDF';
        this.generationError.emit(this.errorMessage);
        this.isGenerating = false;
        reject(error);
      }
    });
  }

  // Constrói a configuração do PDF baseada no tipo
  private buildPdfConfig(): any {
    const config = { ...this.config };

    switch (config.type) {
      case 'financial':
        return this.buildFinancialReportConfig();
      case 'service-order':
        return this.buildServiceOrderConfig();
      default:
        return this.buildCustomConfig();
    }
  }

  // Constrói configuração para relatório financeiro
  private buildFinancialReportConfig(): any {
    const financialData = this.data as FinancialReportData;
    
    // Preparar dados da tabela
    const tableData: any[][] = [];
    
    // Adicionar contas a pagar
    financialData.contasAPagarRecords.forEach(record => {
      tableData.push([
        this.formatCurrency(record.value),
        record.customerName || record.partnerName || 'N/A',
        record.systemName || 'N/A',
        this.formatDate(record.dueDate),
        record.status || 'N/A'
      ]);
    });

    // Adicionar contas a receber
    financialData.contasAReceberRecords.forEach(record => {
      tableData.push([
        this.formatCurrency(record.value),
        record.customerName || record.partnerName || 'N/A',
        record.systemName || 'N/A',
        this.formatDate(record.dueDate),
        record.status || 'N/A'
      ]);
    });

    return {
      reportTitle: 'RELATÓRIO FINANCEIRO - FLUXO DE CAIXA',
      reportInfo: [
        { label: 'Data de Geração', value: financialData.generatedAt.toLocaleDateString('pt-BR') },
        { label: 'Hora de Geração', value: financialData.generatedAt.toLocaleTimeString('pt-BR') },
        { label: 'Total Contas a Pagar', value: this.formatCurrency(financialData.contasAPagarTotal) },
        { label: 'Total Contas a Receber', value: this.formatCurrency(financialData.contasAReceberTotal) },
        { label: 'Saldo', value: this.formatCurrency(financialData.contasAReceberTotal - financialData.contasAPagarTotal) }
      ],
      headers: ['Valor', 'Cliente/Parceiro', 'Sistema', 'Vencimento', 'Status'],
      tableData: tableData,
      type: 'financial'
    };
  }

  // Constrói configuração para ordem de serviço
  private buildServiceOrderConfig(): any {
    return {
      orderDetails: this.data.details || [],
      serviceData: this.data.serviceData || this.data,
      type: 'service-order'
    };
  }

  // Constrói configuração customizada
  private buildCustomConfig(): any {
    return {
      ...this.config,
      details: this.data.details || [],
      headers: this.config.headers || [],
      tableData: this.config.tableData || [],
      customData: this.data
    };
  }

  // Utilitários
  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  private formatDate(date: Date | string | undefined): string {
    if (!date) return 'N/A';
    
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleDateString('pt-BR');
    } catch (error) {
      return 'Data inválida';
    }
  }

  private downloadFile(blob: Blob): void {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
    link.download = this.fileName;
    document.body.appendChild(link);
      link.click();
    document.body.removeChild(link);
      URL.revokeObjectURL(url);
  }

  // Método para regenerar PDF
  retryGeneration(): void {
    this.errorMessage = null;
    if (this.showPreview) {
      this.generatePreview();
    } else {
      this.downloadPDF();
    }
  }
}
